import * as React from 'react';
import { useEffect } from 'react';
import { useIsClientSide } from '../../../../utils/hooks';
import { Cart, CartsDataActions } from '@msdyn365-commerce/retail-proxy';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { IActionContext } from '@msdyn365-commerce/core';
import Modal from '../../../../shared/components/modal.component';
import { clearCookie, getCookie, saveCookie } from '../../../../shared/functions/cookie';


export interface HeaderHelperConfiguration {
    enableRedirectOnCookie?: Boolean;
    pageToRedirectOn?: string;
    redirectsOnFrontPage?: redirectObject[];
    cookieExpiration?: number;
    shouldRedirectToLocale?: Boolean;
    shouldLogOutInvalidLocaleUser?: Boolean;
    removeUserCartItems?: Boolean;
    shouldDisplayCartUpdate?: Boolean;
    cartUpdateMessage?: string;
    defaultButtonMessage?: string;
    cartLink?: string;
    cartLinkButtonMessage?: string;
    cart?: Readonly<Cart>;
    telemetry?: any;
    context?: IActionContext;
}

interface redirectObject {
    mappingToUrl: urlObject;
    mappedFrom: string[];
}

interface urlObject {
    destinationUrl: string;
    type: string;
}

interface CartLine {
    id?: string;
    productId?: number;
    barcode?: string;
    description?: string;
    quantity?: number;
}

export interface HeaderHelperProps {
    config: HeaderHelperConfiguration; 
    request: any; //is any since not exposed from msdynamics modules
}
var savedNewItems: CartLine[] = [];
export const HeaderHelper = ({ config, request }: HeaderHelperProps) => {
    return (
        <> 
            <CookieHandlerHelper config={config} request={request} />
            <LocaleRedirectHelper config={config} request={request} />
            <CartUpdateHelper config={config} request={request} />
        </>
    );
};

const CookieHandlerHelper = ({ config, request }: HeaderHelperProps) => {
    const isClient = useIsClientSide();
    const { enableRedirectOnCookie = false, pageToRedirectOn = '', redirectsOnFrontPage = [], cookieExpiration = 0} = config;
    useEffect(() => {
        //Clause guards
        if (!isClient) {
            return;
        }

        if (!enableRedirectOnCookie) {
            return;
        }
        
        const { pathname } = window.location;
        const cookieName = 'onlyGenderCategory';
        if (pathname === pageToRedirectOn) {
            //If cookie exists, redirect to the page
            if(getCookie(cookieName) !== ''){
                const cookieContent = getCookie(cookieName);
                window.location.replace(cookieContent);
            }
        } else{
            //Set cookie
            const subPaths = pathname.split('/').filter(path => path != '');

            const mappingTo = redirectsOnFrontPage.find(redirect =>
                redirect.mappedFrom.some(fromMapping => subPaths.some(subPath => subPath == fromMapping))
            );
            if (mappingTo == undefined) {
                return;
            }

            const currentDate = new Date();
            currentDate.setDate(currentDate.getDate() + cookieExpiration);
            const expirationDateInUtc = currentDate.toUTCString();
            clearCookie(cookieName);
            document.cookie = `${cookieName}=${mappingTo.mappingToUrl.destinationUrl}; expires=${expirationDateInUtc};`;
            return;
        }
    }, [isClient]);
    return <></>;
};

const LocaleRedirectHelper = ({ config, request }: HeaderHelperProps) => {
    const isClient = useIsClientSide();
    const { shouldRedirectToLocale = false, shouldLogOutInvalidLocaleUser = false, cookieExpiration = 0} = config;
    useEffect(() => {
        //Clause guards
        if (!isClient) {
            return;
        }
        const cookieName = '_msdyn365___site_';

        if(shouldRedirectToLocale) {
            const cookieContent = getCookie(cookieName);
            if(cookieContent !== '' && window.location.pathname === '/') {
                window.location.replace(cookieContent);
            }
        } else {
            //If it should not redirect to locale, then we need to save the locale in a cookie
            var previousCookie = getCookie(cookieName);
            var path = window.location.pathname;
            const subPaths = path.split('/').filter(pathname => pathname != '');
            if(subPaths.length > 0) {
                path = '/' + subPaths[0];
            }

            const currentDate = new Date();
            currentDate.setDate(currentDate.getDate() + cookieExpiration);
            const expirationDateInUtc = currentDate.toUTCString();
            clearCookie(cookieName);
            document.cookie = `${cookieName}=${path}; expires=${expirationDateInUtc}; path=/;`;

            //If the user is logged in on an invalid locale
            if(shouldLogOutInvalidLocaleUser && request.user.isAuthenticated && previousCookie && previousCookie !== path) {
                if(config.telemetry) {
                    //TODO: Should be information telemetry
                    config.telemetry.warning('User logged in on invalid locale. Logging out user.');
                }
                window.location.href = request.user.signOutUrl;
            }
        }
    }, [isClient]);
    //TODO: Also run on isConsentGiven change?
    return <></>;
};

const CartUpdateHelper = ({ config, request }: HeaderHelperProps): JSX.Element => {
    const [isModalOpen, setModalOpen] = React.useState(true);

    const toggleModal = () => {
        setModalOpen(!isModalOpen);
    }

    const isClient = useIsClientSide();
    const { cookieExpiration = 0} = config;
    //Clause guards
    if (!isClient) {
        return <></>;
    }
    var popup: JSX.Element = <></>;
    const cookieName = 'onlyCart';
    const newCart: CartLine[] | undefined = config.cart?.CartLines?.map((item) => {
        return {id: item.LineId, productId: item.ProductId, barcode: item.Barcode, description: item.Description, quantity: item.Quantity};
    });

    //if this has run and identified new items previously, skip the check
    if(savedNewItems && savedNewItems.length > 0) {
        saveCookie(cookieName, newCart, cookieExpiration);
        if(config.shouldDisplayCartUpdate) {
            const defaultButtonAction = config.removeUserCartItems ? () => removeCartLines(savedNewItems, config.context, config.cart?.Id, config.telemetry) : undefined;
            return <Modal isOpen={isModalOpen} toggle={toggleModal} content={<p>{config.cartUpdateMessage}</p>} defaultButtonAction={defaultButtonAction} defaultButtonMessage={config.defaultButtonMessage} buttons={<button className='redirect-btn' onClick={() => redirect(config)}>{config.cartLinkButtonMessage}</button>}></Modal>;
        } else {
            if(config.removeUserCartItems) {
                removeCartLines(savedNewItems, config.context, config.cart?.Id, config.telemetry);
            }
            return <></>;
        }
    }

    if(getCookie(cookieName) && request.user.isAuthenticated) {
        var newItems: any[] = [];
        var blackList: string[] = []; //as there can be multiple lines with the same barcode and quantity we need to keep track of the ones we have already "used"
        const previousCart: CartLine[] = JSON.parse(getCookie(cookieName));
        //if the previous cart is empty, we don't remove new items
        if(previousCart && previousCart.length > 0) { 
            newCart?.forEach((item) => {
                var prevItem: CartLine | undefined = previousCart.find((prevItem: CartLine) => prevItem.barcode === item.barcode && prevItem.quantity === item.quantity && blackList.indexOf(prevItem.id ?? '') === -1);
                if(!prevItem) {
                    newItems.push(item);
                } else {
                    blackList.push(prevItem.id ?? '');
                }
            });
        }
        if(newItems && newItems.length > 0) {
            savedNewItems = newItems;
            config.telemetry.warning('_cart-update Cart change popup displayed');
            if(config.shouldDisplayCartUpdate) {
                const defaultButtonAction = config.removeUserCartItems ? () => removeCartLines(savedNewItems, config.context, config.cart?.Id, config.telemetry) : undefined;
                popup = <Modal isOpen={isModalOpen} toggle={toggleModal} content={<p>{config.cartUpdateMessage}</p>} defaultButtonAction={defaultButtonAction} defaultButtonMessage={config.defaultButtonMessage} buttons={<button className='redirect-btn' onClick={() => redirect(config)}>{config.cartLinkButtonMessage}</button>}></Modal>;
            } else {
                if(config.removeUserCartItems) {
                    removeCartLines(savedNewItems, config.context, config.cart?.Id, config.telemetry);
                }
            }
        }
    }
    saveCookie(cookieName, newCart, cookieExpiration);
    return popup;
};

export const saveCartCookie = (cookieName: string, data: any, cookieExpiration = 30) => { 
    const newCart: CartLine[] | undefined = data?.map((item: any) => {
        return {id: item.LineId, productId: item.ProductId, barcode: item.Barcode, description: item.Description, quantity: item.Quantity};
    });
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + cookieExpiration);
    const expirationDateInUtc = currentDate.toUTCString();
    clearCookie(cookieName);
    document.cookie = `${cookieName}=${JSON.stringify(newCart)}; expires=${expirationDateInUtc}; path=/;`;
}

const removeCartLines = (cartLines?: CartLine[], context?: IActionContext, id?: string, telemetry?: any) => {
    var cartLineIds = cartLines?.map((cartLine: CartLine) => cartLine.id ?? '');
    if(cartLineIds && context && id) { 
        CartsDataActions
                .removeCartLinesAsync({ callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) }, id, cartLineIds)
                .then(() => {
                    //reload the page to update the cart
                    telemetry.warning('_cart-update Cart change popup removing cart lines');
                    window.location.reload();
                })
                .catch(error => {
                    telemetry.exception(error);
                });
    }
}

const redirect = (config: HeaderHelperConfiguration) => { 
    config.telemetry.warning('_cart-update Cart change popup redirecting to cart');
    window.location.href = config.cartLink ?? '';
}